import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  ProjectDetailsRow,
  InputError,
  EditButton,
  ProjectDescriptionTitle,
  StyledInlineRow,
  ProjectDetailsGridContainer,
  StyledProjectDetailsContainer
} from './ProjectDescriptionStyle'
import {InputTitleNormal} from '../../common/TitleStyle'
import Grid from '@mui/material/Grid'
import InlineErrors from '../../common/Errors/InlineErrors.json'
import {specialCharactersForDescription} from '../../ProjectDetails/ProjectDescription/regexData'
import {useParams} from 'react-router-dom'
import RequirementBox from '../../common/RequirementBox'
import {useProjectDetails} from '../../../store/hooks/projectDetails'
import CustomBox from '../../common/../../components/common/CustomBox'
import buttonTitles from '../../../appConfig/buttonTitles.config.json'
import PropTypes from 'prop-types'
import CreateIcon from '@mui/icons-material/Create'
import TextFieldWithCounter from '../../common/TextFieldWithCounter/TextFieldWithCounter'
import TextAreaWithCounter from '../../common/TextAreaWithCounter/TextAreaWithCounter'
import {VALIDATION_MESSAGES, VALIDATION_PATTERNS} from '../../common/Helpers/formValidation'
import TruncatedTextWithPopover from '../../common/TruncatedTextWithPopover/TruncatedTextWithPopover'

const PROJECT_NAME_MAX_LENGTH = 50
const PROJECT_DESCRIPTION_MAX_LENGTH = 100

const ProjectDescriptionComponent = ({
  create,
  projectName,
  setProjectName,
  projectDescription,
  setProjectDescription,
  currentProjectName,
  errorProjectDetails
}) => {
  ProjectDescriptionComponent.propTypes = {
    create: PropTypes.bool.isRequired,
    projectName: PropTypes.string.isRequired,
    setProjectName: PropTypes.func.isRequired,
    projectDescription: PropTypes.string.isRequired,
    setProjectDescription: PropTypes.func.isRequired,
    currentProjectName: PropTypes.string.isRequired,
    errorProjectDetails: PropTypes.object.isRequired
  }

  const {projectNamesList} = useProjectDetails()
  const {inputDisabled, setInputDisabled, setErrorTextEnabled} = errorProjectDetails || {}
  const {projectId} = useParams()
  const [projectNameDraft, setProjectNameDraft] = useState(projectName)
  const [projectDescriptionDraft, setProjectDescriptionDraft] = useState(projectDescription)
  const [errorProjectDescriptionText] = useState(InlineErrors.speicalCharactersInDescriptionError)
  const [errorProjectDescriptionTextEnabled, setErrorProjectDescriptionTextEnabled] = useState(false)
  const projectNameSyncTimeoutRef = useRef(null)
  const projectDescriptionSyncTimeoutRef = useRef(null)

  const trimmedProjectName = projectNameDraft.trim()
  const trimmedProjectDescription = projectDescriptionDraft.trim()

  const normalizedProjectNamesSet = useMemo(
    () => new Set((projectNamesList || []).map((name) => (name || '').trim())),
    [projectNamesList]
  )

  const isDuplicateProjectName =
    trimmedProjectName.length > 0 &&
    normalizedProjectNamesSet.has(trimmedProjectName) &&
    trimmedProjectName !== (currentProjectName || '').trim()

  const isProjectNameOverLimit = projectNameDraft.length > PROJECT_NAME_MAX_LENGTH

  let projectNameErrorMessage = ''
  if (isProjectNameOverLimit) {
    projectNameErrorMessage = VALIDATION_MESSAGES.MAX_LENGTH(PROJECT_NAME_MAX_LENGTH)
  } else if (projectNameDraft.length > 0 && trimmedProjectName.length === 0) {
    projectNameErrorMessage = VALIDATION_MESSAGES.REQUIRED('Project name')
  } else if (isDuplicateProjectName) {
    projectNameErrorMessage = InlineErrors.duplicateName
  } else if (trimmedProjectName.length > 0 && !VALIDATION_PATTERNS.ENTITY_NAME.test(trimmedProjectName)) {
    projectNameErrorMessage = VALIDATION_MESSAGES.INVALID_ENTITY_NAME
  }

  const hasDescriptionSpecialCharacters =
    trimmedProjectDescription.length > 0 && Boolean(trimmedProjectDescription.match(specialCharactersForDescription))
  const isProjectDescriptionOverLimit = projectDescriptionDraft.length > PROJECT_DESCRIPTION_MAX_LENGTH

  useEffect(() => {
    setProjectNameDraft(projectName)
  }, [projectName])

  useEffect(() => {
    setProjectDescriptionDraft(projectDescription)
  }, [projectDescription])

  useEffect(() => {
    return () => {
      if (projectNameSyncTimeoutRef.current) {
        clearTimeout(projectNameSyncTimeoutRef.current)
      }
      if (projectDescriptionSyncTimeoutRef.current) {
        clearTimeout(projectDescriptionSyncTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    setErrorTextEnabled(Boolean(projectNameErrorMessage) || isProjectDescriptionOverLimit)
  }, [isProjectDescriptionOverLimit, projectNameErrorMessage, setErrorTextEnabled])

  const handleProjectNameInput = useCallback(
    (event) => {
      const inputValue = event.target.value
      setProjectNameDraft(inputValue)

      if (projectNameSyncTimeoutRef.current) {
        clearTimeout(projectNameSyncTimeoutRef.current)
      }
      projectNameSyncTimeoutRef.current = setTimeout(() => {
        setProjectName(inputValue)
      }, 120)
    },
    [setProjectName]
  )

  const enableInput = () => {
    setInputDisabled(false)
  }

  const handleProjectDescriptionInput = useCallback(
    (event) => {
      const inputValue = event.target.value
      setProjectDescriptionDraft(inputValue)
      const hasSpecialChars = Boolean(inputValue.trim().match(specialCharactersForDescription))
      const isOverLimit = inputValue.length > PROJECT_DESCRIPTION_MAX_LENGTH
      setErrorProjectDescriptionTextEnabled(hasSpecialChars || isOverLimit)

      if (projectDescriptionSyncTimeoutRef.current) {
        clearTimeout(projectDescriptionSyncTimeoutRef.current)
      }
      projectDescriptionSyncTimeoutRef.current = setTimeout(() => {
        setProjectDescription(inputValue)
      }, 120)
    },
    [setProjectDescription]
  )

  const flushProjectName = useCallback(() => {
    if (projectNameSyncTimeoutRef.current) {
      clearTimeout(projectNameSyncTimeoutRef.current)
    }
    setProjectName(projectNameDraft)
  }, [projectNameDraft, setProjectName])

  const flushProjectDescription = useCallback(() => {
    if (projectDescriptionSyncTimeoutRef.current) {
      clearTimeout(projectDescriptionSyncTimeoutRef.current)
    }
    setProjectDescription(projectDescriptionDraft)
  }, [projectDescriptionDraft, setProjectDescription])

  return (
    <Grid>
      <ProjectDetailsRow>
        <ProjectDescriptionTitle tabIndex={0}>Project Details</ProjectDescriptionTitle>
        {!create && projectId && (
          <EditButton
            data-testid='edit-button'
            title={buttonTitles.editProjectName}
            onClick={enableInput}
            aria-label='edit-project-details'
          >
            <CreateIcon variant='primaryIcon' />
          </EditButton>
        )}
      </ProjectDetailsRow>
      <ProjectDetailsGridContainer container columns={12} spacing={{xs: 2}}>
        <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
          <StyledInlineRow>
            <InputTitleNormal variant='subtitleSmall'>Project Name:</InputTitleNormal>
          </StyledInlineRow>
          {inputDisabled && !create && (
            <TruncatedTextWithPopover dataTestId='project-name-display'>{projectName}</TruncatedTextWithPopover>
          )}
          {(!inputDisabled || create) && (
            <TextFieldWithCounter
              label=''
              name='projectName'
              disabled={!create && projectId && inputDisabled}
              value={projectNameDraft}
              maxLength={PROJECT_NAME_MAX_LENGTH}
              enforceMaxLength={false}
              fullWidth
              size='medium'
              error={Boolean(projectNameErrorMessage)}
              helperText={projectNameErrorMessage}
              onChange={handleProjectNameInput}
              textFieldProps={{
                margin: 'dense',
                id: 'projectName',
                placeholder: 'Enter project name',
                inputProps: {
                  'data-testid': 'project-name-input',
                  'aria-labelledby': 'projectName'
                },
                'aria-label': 'enter-project-name',
                onBlur: flushProjectName
              }}
            />
          )}
          <CustomBox
            tabIndex={0}
            aria-label={`${projectNameErrorMessage}-in-project-name`}
            hidden={!projectNameErrorMessage}
          />
          {!inputDisabled || create ? <RequirementBox projectReq={true} props={'Project'} /> : ''}
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
          <StyledInlineRow>
            <InputTitleNormal variant='subtitleSmall'>Project Description:</InputTitleNormal>
          </StyledInlineRow>
          {inputDisabled && !create && (
            <TruncatedTextWithPopover dataTestId='project-description-display'>
              {projectDescription}
            </TruncatedTextWithPopover>
          )}
          {(!inputDisabled || create) && (
            <StyledProjectDetailsContainer>
              <TextAreaWithCounter
                label=''
                name='projectDescription'
                disabled={!create && projectId && inputDisabled}
                value={projectDescriptionDraft}
                maxLength={PROJECT_DESCRIPTION_MAX_LENGTH}
                fullWidth
                size='medium'
                minRows={1}
                error={hasDescriptionSpecialCharacters || isProjectDescriptionOverLimit}
                helperText=''
                onChange={handleProjectDescriptionInput}
                textAreaProps={{
                  inputProps: {
                    'data-testid': 'project-desc-input',
                    'aria-labelledby': 'description',
                    id: 'description'
                  },
                  'aria-label': 'enter-project-description',
                  'aria-describedby': 'project-desc-error',
                  onBlur: flushProjectDescription
                }}
              />
            </StyledProjectDetailsContainer>
          )}
          <CustomBox
            tabIndex={0}
            aria-label={`${errorProjectDescriptionText}-in-project-description`}
            aria-live='polite'
            id='project-desc-error'
            hidden={!errorProjectDescriptionTextEnabled}
          >
            <InputError>{hasDescriptionSpecialCharacters && errorProjectDescriptionText}</InputError>
          </CustomBox>
        </Grid>
      </ProjectDetailsGridContainer>
    </Grid>
  )
}

export default ProjectDescriptionComponent

/* Function Descriptions:
  - handleProjectNameInput: Validates project name input and debounces sync to parent state.
  - enableInput: Enables the input fields for editing.
  - handleProjectDescriptionInput: Validates project description input and debounces sync to parent state.
*/
